import jwt from 'jsonwebtoken';
import throttle from 'lodash/throttle';
import fetch from 'isomorphic-unfetch';
import cookie from 'component-cookie';

// How often to check if the token needs to be refreshed.
const DEFAULT_REFRESH_INTERVAL = 5 * 60 * 1000; // every 5 minutes

// Percentage of the token lifetime before which the token shouldn't be refreshed.
// (e.g. a 30m token shouldn't be refreshed unless it expires within 9 minutes if 70%)
const DEFAULT_REFRESH_THRESHOLD_PERCENTAGE = 0.7;

// The amount of time after a reset after which a token should be allowed to expire.
// (if set to 2 hours, a 30m token should be reset for the last time 1.5h after last reset)
// It will never take longer than this timeout to expire, but may expire early by
// up to DEFAULT_REFRESH_INTERVAL.
const DEFAULT_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours

export type TokenRefresherProps = {
  cookieDomain: () => string;
  authEndpoint?: string;
  cookieName: () => string;
  refreshInterval?: number;
  newTokenCallback?: (arg1: string) => void;
  refreshThresholdPercentage?: number;
  timeout?: number;
};

export class TokenRefresher {
  protected options: TokenRefresherProps;

  private cookieDomain: string;

  private interval: undefined | number | NodeJS.Timeout;

  public throttledReset: undefined | (() => Promise<void>);

  private lastResetDate?: Date;

  constructor(options: TokenRefresherProps) {
    this.options = options;
    this.cookieDomain = options.cookieDomain();
    this.throttledReset = undefined;
  }

  getToken() {
    return cookie(this.options.cookieName());
  }

  getDomain() {
    return this.cookieDomain;
  }

  async resetTimeout() {
    if (!this.throttledReset) {
      this.throttledReset = throttle(async () => {
        await this._resetTimeout();
      }, 5000);
    }

    return this.throttledReset();
  }

  async _resetTimeout() {
    const { refreshInterval = DEFAULT_REFRESH_INTERVAL } = this.options;

    this.stop();

    this.lastResetDate = new Date();
    await this._refreshToken();
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.interval = setInterval(async () => {
      await this._refreshToken();
    }, refreshInterval);
  }

  stop() {
    clearInterval(this.interval);
  }

  async _refreshToken() {
    const { authEndpoint = '' } = this.options;

    if (!this._shouldRefreshToken()) {
      return;
    }

    let response;
    try {
      response = await fetch(authEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: this.getToken() }),
      });
    } catch {
      // Just swallow the error, it will try again on the next refreshInterval.
      return;
    }

    // Just swallow the error, it will try again on the next refreshInterval.
    if (response.status !== 200) {
      return;
    }

    const { data: newToken } = await response.json();

    const { newTokenCallback } = this.options;
    if (newTokenCallback) {
      newTokenCallback(newToken);
    }
  }

  // Has side effects. If the token should never be refreshed, stops.
  _shouldRefreshToken() {
    const {
      refreshThresholdPercentage = DEFAULT_REFRESH_THRESHOLD_PERCENTAGE,
      timeout = DEFAULT_TIMEOUT,
    } = this.options;

    const token = this.getToken();

    if (!token) {
      this.stop();
      return false;
    }

    const decodedToken = jwt.decode(token, { json: true });

    const currentDate = Date.now();
    const expirationDate = new Date((decodedToken?.exp ?? 0) * 1000).getTime();
    const issuedDate = new Date((decodedToken?.iat ?? 0) * 1000).getTime();

    const tokenLifetimeLength = expirationDate - issuedDate;
    const finalResetDate = new Date(
      (this.lastResetDate?.getTime() ?? 0) + timeout - tokenLifetimeLength
    ).getTime();

    if (
      // If the token is already expired, can't refresh.
      expirationDate < currentDate ||
      // If timeout hasn't been restarted recently enough, stop refreshing token.
      currentDate > finalResetDate
    ) {
      this.stop();
      return false;
    }

    // If the token has been recently refreshed (perhaps in another tab), skip this refresh.
    const refreshThreshold = tokenLifetimeLength * refreshThresholdPercentage;
    return currentDate - issuedDate >= refreshThreshold;
  }
}
